import { supabase } from '../lib/supabaseClient';
import { GameSession, CreateGameRequest, JoinGameRequest, GameAction } from '../types/game';

export async function createGameSession(request: CreateGameRequest): Promise<GameSession> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('Unauthorized');
  }

  // 덱 정보 가져오기
  const { data: deck, error: deckError } = await supabase
    .from('decks')
    .select('card_ids')
    .eq('id', request.deck_id)
    .eq('user_id', session.user.id)
    .single();

  if (deckError || !deck) {
    throw new Error('Deck not found');
  }

  // 덱 셔플 함수
  const shuffleDeck = (deck: number[]): number[] => {
    const shuffled = [...deck];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // 초기 게임 상태 생성
  const shuffledDeck = shuffleDeck(deck.card_ids);
  const initialGameState = {
    player1: {
      user_id: session.user.id,
      life: 20,
      hand: [],
      deck: shuffledDeck,
      field: [],
      graveyard: [],
      mana: 0,
      max_mana: 0,
    },
    player2: {
      user_id: '',
      life: 20,
      hand: [],
      deck: [],
      field: [],
      graveyard: [],
      mana: 0,
      max_mana: 0,
    },
    turn: 1,
    phase: 'waiting' as const,
  };

  const { data, error } = await supabase
    .from('game_sessions')
    .insert({
      player1_id: session.user.id,
      player2_id: null,
      current_turn_user_id: session.user.id,
      game_state: initialGameState,
      status: 'waiting',
      winner_id: null,
    })
    .select()
    .single();

  if (error) {
    throw error;
  }

  return data;
}

export async function joinGameSession(request: JoinGameRequest): Promise<GameSession> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('Unauthorized');
  }

  // 덱 정보 가져오기
  const { data: deck, error: deckError } = await supabase
    .from('decks')
    .select('card_ids')
    .eq('id', request.deck_id)
    .eq('user_id', session.user.id)
    .single();

  if (deckError || !deck) {
    throw new Error('Deck not found');
  }

  // 게임 세션 가져오기
  const { data: gameSession, error: gameError } = await supabase
    .from('game_sessions')
    .select('*')
    .eq('id', request.game_session_id)
    .eq('status', 'waiting')
    .single();

  if (gameError || !gameSession) {
    throw new Error('Game session not found or not available');
  }

  if (gameSession.player1_id === session.user.id) {
    throw new Error('Cannot join your own game');
  }

  // 덱 셔플 함수
  const shuffleDeck = (deck: number[]): number[] => {
    const shuffled = [...deck];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // 게임 상태 업데이트
  const gameState = gameSession.game_state as any;
  const shuffledDeck = shuffleDeck(deck.card_ids);
  
  gameState.player2 = {
    user_id: session.user.id,
    life: 20,
    hand: [],
    deck: shuffledDeck,
    field: [],
    graveyard: [],
    mana: 0,
    max_mana: 0,
  };
  gameState.phase = 'draw';
  
  // 첫 턴 시작 처리 (player1)
  // 마나 설정 및 초기 드로우는 Edge Function에서 처리하거나 여기서 처리
  gameState.player1.max_mana = 1;
  gameState.player1.mana = 1;
  
  // 초기 드로우 (각 플레이어 5장)
  const drawInitialCards = (player: any, count: number = 5) => {
    for (let i = 0; i < count && player.deck.length > 0; i++) {
      const card = player.deck.shift();
      if (card) {
        player.hand.push(card);
      }
    }
  };
  
  drawInitialCards(gameState.player1, 5);
  drawInitialCards(gameState.player2, 5);
  
  // 첫 턴 시작 처리 (player1)
  // player1의 첫 턴: 마나 1, 카드 드로우 1장
  gameState.player1.max_mana = 1;
  gameState.player1.mana = 1;
  if (gameState.player1.deck.length > 0) {
    const firstCard = gameState.player1.deck.shift();
    if (firstCard) {
      gameState.player1.hand.push(firstCard);
    }
  }
  gameState.phase = 'main'; // 첫 턴은 main 페이즈로 시작

  // 동시 참여 방지: player2_id가 null인 상태에서만 업데이트 (원자적 연산)
  const { data, error } = await supabase
    .from('game_sessions')
    .update({
      player2_id: session.user.id,
      game_state: gameState,
      status: 'playing',
      current_turn_user_id: gameSession.player1_id, // player1의 턴으로 시작
      last_action_at: new Date().toISOString(), // 게임 시작 시 마지막 액션 시간 설정
    })
    .eq('id', request.game_session_id)
    .is('player2_id', null) // player2_id가 null인 경우에만 업데이트
    .eq('status', 'waiting') // status도 다시 확인
    .select()
    .single();

  if (error) {
    throw error;
  }

  if (!data) {
    // 다른 플레이어가 이미 참여했거나 게임 상태가 변경됨
    throw new Error('Game session is no longer available or already started');
  }

  return data;
}

export async function submitGameAction(
  gameSessionId: string,
  action: GameAction
): Promise<GameSession> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('Unauthorized');
  }

  // Edge Function으로 게임 액션 처리
  const { data, error } = await supabase.functions.invoke('game-action', {
    body: {
      game_session_id: gameSessionId,
      action,
    },
  });

  if (error) {
    // 에러 응답에서 상태 코드와 메시지 추출
    const errorMessage = error.message || 'Unknown error';
    const errorObj = error as any;
    errorObj.status = errorObj.status || 500;
    errorObj.message = errorMessage;
    throw errorObj;
  }

  if (!data) {
    throw new Error('No data returned from game action');
  }

  return data;
}

export async function getGameSession(gameSessionId: string): Promise<GameSession | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('Unauthorized');
  }

  const { data, error } = await supabase
    .from('game_sessions')
    .select('*')
    .eq('id', gameSessionId)
    .single();

  if (error) {
    return null;
  }

  // 자신의 게임인지 확인
  if (data.player1_id !== session.user.id && data.player2_id !== session.user.id) {
    return null;
  }

  return data;
}

export async function getWaitingGames(): Promise<GameSession[]> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('Unauthorized');
  }

  const { data, error } = await supabase
    .from('game_sessions')
    .select('*')
    .eq('status', 'waiting')
    .neq('player1_id', session.user.id) // 자신의 게임 제외
    .is('player2_id', null)
    .order('created_at', { ascending: false })
    .limit(10);

  if (error) {
    throw error;
  }

  return data || [];
}

export async function deleteGameSession(gameSessionId: string): Promise<void> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('Unauthorized');
  }

  // 자신이 생성한 대기 중인 게임만 삭제 가능
  const { error } = await supabase
    .from('game_sessions')
    .delete()
    .eq('id', gameSessionId)
    .eq('player1_id', session.user.id)
    .eq('status', 'waiting')
    .is('player2_id', null);

  if (error) {
    throw error;
  }
}

